import MeetingAudioPlayer from "@/components/audio-player";
import { useState, useEffect, Fragment } from "react";
import { useSession, useUser } from "@clerk/react";
import {
  Accordion,
  Button,
  Card,
  Drawer,
  Avatar,
  useOverlayState,
  Skeleton,
  AlertDialog,
  toast,
  Spinner,
} from "@heroui/react";
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  Clock,
  MapPin,
  Users,
  AudioLines,
  StickyNote,
  Download,
  Mail,
  Trash2,
  CalendarX2,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getByShortCode as getByShortCodeApi, deleteMeet as deleteMeetApi } from "@/api/meets";
import { getAudioDownloadUrl as getAudioDownloadUrlApi } from "@/api/audio";
import { formatDate, isMeetingPast, isMeetingUpcoming } from "@/utils/dates";
import MemberAvatar from "../members/member-avatar";
import EditMeetsDetailsComp from "./edit-meets-details-comp";
import OpenInMapsDrawer from "../drawers/open-in-maps-drawer";
import { getSummaryByMeetsId as getSummaryByMeetsIdApi } from "@/api/meets-summary";
import { getDetailedByMeetsId as getDetailedByMeetsIdApi } from "@/api/meets-detailed";
import { getAvatarUrl } from "@/utils/avatars";
import PulledDownWrapper from "../wrappers/pulled-down-wrapper";

const MeetingDetailPill = ({
  icon: Icon,
  imgSrc,
  iconNode,
  title,
  sub,
  interactive,
  onPress,
  affixIcon: AffixIcon,
  onAffixPress,
  affixLabel,
  onAffixLabelPress,
}) => {
  const badge = iconNode ?? (
    <div className="bg-accent-soft dark:bg-[#4E4E9838] glass-box-shadow text-accent border-glass-border flex size-9 shrink-0 items-center justify-center rounded-full border">
      {Icon && <Icon size={16} />}

      {imgSrc && <img src={imgSrc} />}
    </div>
  );

  const content = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        {badge}
        <div className="flex min-w-0 flex-col items-start gap-0.5 text-left">
          <span className="text-sm font-bold text-foreground">{title}</span>
          <span className="w-full truncate text-sm font-normal text-muted dark:text-white">{sub}</span>
        </div>
      </div>

      {interactive && !affixLabel && <ChevronRight className="size-4 shrink-0 text-[#9896BE]" />}

      {!interactive && AffixIcon && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAffixPress?.();
          }}
          className="bg-accent-soft dark:[#000000B2] text-accent border-glass-border flex size-9 shrink-0 items-center justify-center rounded-full border"
        >
          <AffixIcon size={16} />
        </button>
      )}
    </>
  );

  if (interactive && affixLabel) {
    return (
      <div className="bg-white/68 dark:bg-[#000000B2] flex w-full items-center justify-between gap-3 rounded-[18px] px-4.5 py-4">
        <button type="button" onClick={onPress} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          {content}
        </button>
        <Button
          size="sm"
          variant="tertiary"
          className="shrink-0 rounded-full whitespace-nowrap"
          onPress={onAffixLabelPress}
        >
          {affixLabel}
        </Button>
      </div>
    );
  }

  if (interactive) {
    return (
      <Button
        className="bg-white/68 dark:bg-[#000000B2] w-full justify-between rounded-[18px] px-4.5 py-8"
        onPress={onPress}
      >
        {content}
      </Button>
    );
  }

  return (
    <div className="bg-white/68 dark:bg-[#000000B2] flex w-full items-center justify-between rounded-[18px] px-3.5 py-3">
      {content}
    </div>
  );
};

const DetailsSkeleton = () => (
  <div className="flex flex-col gap-3 mt-20">
    <Skeleton className="size-9 rounded-full" />
    <Skeleton className="mt-2 h-8 w-2/3 rounded-lg" />
    <Skeleton className="h-4 w-1/3 rounded-lg" />
    <div className="mt-4 flex flex-col gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-[72px] w-full rounded-[18px]" />
      ))}
    </div>
  </div>
);

// Mirrors ConnectionNotFound's pattern from connection-details-page.jsx.
const MeetingNotFound = ({ onBack }) => (
  <div className="flex flex-col gap-8 pb-40 mt-20">
    <Button isIconOnly className={"size-9 icon-button"} onPress={onBack}>
      <ArrowLeft size={12} className="text-[#5C5C5C] dark:text-white size-4" />
    </Button>

    <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none">
      <Card.Content className="flex flex-col items-center gap-4 py-10 text-center">
        <div className="size-14 rounded-full flex items-center justify-center text-accent bg-white dark:bg-[#141414]">
          <CalendarX2 size={24} />
        </div>

        <div className="flex flex-col gap-1">
          <span className="font-bold text-[#2A2760] dark:text-white text-lg">Could not find this meeting</span>
          <span className="text-sm text-[#5C5C5C] dark:text-[#9896BE]">
            It may have been deleted, or the link might be incorrect.
          </span>
        </div>

        <Button variant="primary" className="rounded-full mt-2" onPress={onBack}>
          Back Home
        </Button>
      </Card.Content>
    </Card>
  </div>
);

const ViewMeetingDetailsComp = ({ _shortCode, readonly }) => {
  const { session } = useSession();
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mapsState = useOverlayState();
  const [summaries, setSummaries] = useState([]);
  const [detaileds, setDetaileds] = useState([]);

  const [record, setRecord] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isMeetsFinished, setIsMeetsFinished] = useState(false);
  const [isMeetsStillUpcoming, setIsMeetsStillUpcoming] = useState(false);

  const editSection = !readonly ? searchParams.get("edit") : null;

  const audioState = useOverlayState();
  const summaryState = useOverlayState();
  const detailedState = useOverlayState();

  useEffect(() => {
    getByShortCode();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getByShortCode() {
    const token = await session.getToken();
    getByShortCodeApi(_shortCode, token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          const record = _resp.data.Record;
          setRecord(record);
          const meets_date = record["meeting_date"];
          const start_time = record["start_time"]["set"];
          const end_time = (record["end_time"]["real"] || record["end_time"]["set"]);
          const is_past = isMeetingPast(meets_date, end_time);
          setIsMeetsFinished(is_past);
          if (is_past) {
            getMeetsSummaries(record["_id"]);
            getMeetsDetailed(record["_id"]);
            setIsMeetsStillUpcoming(false);
          } else {
            const is_upcoming = isMeetingUpcoming(meets_date, start_time);
            setIsMeetsStillUpcoming(is_upcoming);
          }
        } else {
          setNotFound(true);
        }
      })
      .catch((_resp) => {
        console.log(_resp);
        setNotFound(true);
      });
  }

  function openEdit(section) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("edit", section);
      return next;
    });
  }

  function closeEdit() {
    setSearchParams({}, { replace: true });
  }

  function handleEditSaved(updatedRecord) {
    setRecord(updatedRecord);
    closeEdit();
  }

  async function onDeleteConfirm() {
    const token = await session.getToken();
    setDeleting(true);
    try {
      deleteMeetApi(record._id, token)
        .then((_resp) => {
          if (_resp.data.Result === "SUCCESS") {
            toast.success("Meeting deleted");
            navigate("/", { replace: true });
          } else {
            toast.warning("Failed to delete meeting");
          }
        })
        .catch((err) => console.log(err))
        .finally(() => setDeleting(false));
    } catch (err) {
      console.error(err);
      toast.danger("Something went wrong", { description: "Please try again." });
    } finally {
      setDeleting(false);
    }
  }

  async function onAudioDownloadClick() {
    try {
      const token = await session.getToken();
      const { data } = await getAudioDownloadUrlApi(record._id, token);
      if (!data.url) throw new Error("Missing download URL");
      window.location.assign(data.url);
    } catch {
      toast.warning("Recording is not ready to download. Please try again.");
    }
  }


  async function getMeetsSummaries(_meets_id) {
    const token = await session.getToken();
    getSummaryByMeetsIdApi(_meets_id, token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setSummaries(_resp.data.Records);
        } else {
          toast.warning("Failed to load meeting summaries");
        }
      })
      .catch(() => setSummaries([]));
  }

  async function getMeetsDetailed(_meets_id) {
    const token = await session.getToken();
    getDetailedByMeetsIdApi(_meets_id, token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setDetaileds(_resp.data.Records);
        } else {
          toast.warning("Failed to loading detailed meeting notes");
        }
      })
      .catch(() => setDetaileds([]));
  }

  if (notFound) return <MeetingNotFound onBack={() => navigate("/")} />;

  if (record === null) return <DetailsSkeleton />;

  const isHost = user["id"] === record["created_by"]["user_id"];
  const participants = record["members"] ?? [];
  const participantCount = participants.length;

  const hasLocation = Boolean(record["location"]["name"] !== 'Not Set' && record["location"]["name"]);
  const locationAddress = record["location"]?.name;

  return (
    <div className="mt-16">
      <PulledDownWrapper onRefresh={getByShortCode}>
        {editSection ? (
          <EditMeetsDetailsComp section={editSection} record={record} onCancel={closeEdit} onSaved={handleEditSaved} />
        ) : (
          <div className="flex flex-col gap-4 pb-24">
            <div className="flex items-center justify-between">
              <Button isIconOnly className="icon-button size-9" onPress={() => navigate("/")}>
                <ArrowLeft className="size-4 text-[#5C5C5C] dark:text-white" />
              </Button>

              {isHost && !readonly && (
                <AlertDialog>
                  <Button slot="trigger" isIconOnly size="md" variant="danger-soft" className="rounded-full">
                    <Trash2 size={16} />
                  </Button>
                  <AlertDialog.Backdrop>
                    <AlertDialog.Container placement="center">
                      <AlertDialog.Dialog className="sm:max-w-[400px]">
                        <AlertDialog.CloseTrigger isDisabled={deleting} />
                        <AlertDialog.Header>
                          <AlertDialog.Icon status="danger" />
                          <AlertDialog.Heading>Delete this meeting?</AlertDialog.Heading>
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                          <p>
                            This will permanently delete <strong className="text-foreground">{record["title"]}</strong>{" "}
                            and all of its data including summaries and audio. This action cannot be undone.
                          </p>
                        </AlertDialog.Body>
                        <AlertDialog.Footer>
                          <Button slot="close" variant="tertiary" isDisabled={deleting}>
                            Cancel
                          </Button>
                          <Button variant="danger" isPending={deleting} onPress={onDeleteConfirm}>
                            {deleting && <Spinner color="white" />}
                            Delete Meeting
                          </Button>
                        </AlertDialog.Footer>
                      </AlertDialog.Dialog>
                    </AlertDialog.Container>
                  </AlertDialog.Backdrop>
                </AlertDialog>
              )}
            </div>

            <div className="flex flex-col gap-0">
              <span className="text-[30px] font-extrabold capitalize leading-tight text-foreground">
                {record["title"] || "Untitled Meeting"}
              </span>
              <span className="font-normal text-foreground first-letter:uppercase">
                {record["description"] || "No description..."}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <MeetingDetailPill
                // icon={CalendarDays}
                imgSrc={"/calendar-purple.png"}
                title={formatDate(record["meeting_date"])}
                sub={
                  <span className="inline-flex items-center gap-1">
                    <Clock size={10} className="text-accent size-4" />
                    {record["start_time"]["set"]} - {(record["end_time"]["real"] || record["end_time"]["set"])}
                  </span>
                }
                interactive={!readonly}
                onPress={readonly ? undefined : () => openEdit("date")}
              />

              <MeetingDetailPill
                // icon={MapPin}
                imgSrc={"/map-pin-purple.png"}
                title="Location"
                sub={locationAddress || "No location added"}
                interactive={readonly ? hasLocation : true}
                onPress={
                  readonly ? (record.location.name !== "" ? mapsState.open : undefined) : () => openEdit("location")
                }
              />

              {readonly ? (
                participants.length > 0 && (
                  <Accordion
                    variant="surface"
                    className="border bg-white/68 dark:bg-[#000000B2] border-accent/10 rounded-2xl dark:shadow-none"
                  >
                    <Accordion.Item>
                      <Accordion.Heading>
                        <Accordion.Trigger className="flex items-center justify-between gap-2 px-3.5! py-3.5 rounded-2xl transition-colors text-left w-full">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-9 h-9 glass-box-shadow shrink-0 rounded-full bg-accent-soft dark:bg-[#4E4E9838] flex items-center justify-center">
                              {/* <Users size={20} strokeWidth={1} className="text-accent dark:text-white" /> */}
                              <img src="/users-purple.png" />
                            </div>
                            <span className="font-semibold truncate">
                              {participantCount} Participant{participantCount !== 1 ? "s" : ""}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <div className="flex -space-x-2 items-center">
                              {participants.slice(0, 3).map((member) => (
                                <MemberAvatar
                                  key={member.member_id}
                                  member={member}
                                  fullname={member.member_fullname || "Jane Smith"}
                                  color="accent"
                                  className="ring-2 ring-white dark:ring-[#FFFFFF26]"
                                />
                              ))}
                              {participantCount > 3 && (
                                <span className="ring-2 ring-white dark:ring-[#FFFFFF26] w-8 h-8 rounded-full bg-accent-soft dark:bg-[#FFFFFF21] text-accent dark:text-white text-xs font-semibold flex items-center justify-center">
                                  +{participantCount - 3}
                                </span>
                              )}
                            </div>
                            <Accordion.Indicator className="text-[#9896BE] size-4" strokeWidth={1} />
                          </div>
                        </Accordion.Trigger>
                      </Accordion.Heading>

                      <Accordion.Panel>
                        <Accordion.Body className="flex flex-col gap-3 px-3 pt-2">
                          {participants.map((member) => (
                            <div key={member.member_id} className="flex items-center gap-2">
                              <MemberAvatar
                                member={member}
                                color="accent"
                                fullname={member.member_fullname || "Jane Smith"}
                              />
                              <span className="font-bold capitalize text-sm">
                                {member.member_fullname || "Jane Smith (Fullname not provided)"}
                              </span>
                            </div>
                          ))}
                        </Accordion.Body>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                )
              ) : (
                <MeetingDetailPill
                  // icon={Users}
                  imgSrc={"/users-purple.png"}
                  title="Participants"
                  sub={`${participantCount} ${participantCount === 1 ? "person" : "people"} added`}
                  interactive
                  onPress={() => openEdit("participants")}
                />
              )}

              {isMeetsFinished && (
                <MeetingDetailPill
                  // icon={AudioLines}
                  imgSrc={"/audio-purple.png"}
                  title="Audio"
                  sub="Click to download"
                  affixIcon={Download}
                  onAffixPress={audioState.open}
                />
              )}

              <MeetingDetailPill
                iconNode={
                  <Avatar className="size-9 shrink-0 border-glass-foreground border glass-box-shadow " variant="soft" color="accent">
                    <Avatar.Image
                      // src={`https://api.dicebear.com/9.x/thumbs/svg?seed=${record["created_by"]["user_email"]}`}
                      // src={getAvatarUrl(record["created_by"]["user_email"])}
                      alt="Host avatar"
                    />
                    <Avatar.Fallback className="font-semibold">
                      {record["created_by"]["user_email"].slice(0, 2).toUpperCase()}
                    </Avatar.Fallback>
                  </Avatar>
                }
                title="Hosted by"
                sub={record["created_by"]["user_email"]}
              />

              {isMeetsFinished && (
                <MeetingDetailPill
                  icon={StickyNote}
                  title="Summary"
                  sub={summaries.length > 0 ? summaries[0]["text"] : "Still processing..."}
                  interactive={summaries.length > 0}
                  onPress={summaries.length > 0 ? summaryState.open : undefined}
                  affixLabel={detaileds.length > 0 ? "View Detailed" : undefined}
                  onAffixLabelPress={detailedState.open}
                />
              )}
            </div>
          </div>
        )}
      </PulledDownWrapper>

      {/* Audio drawer — only ever opened when isMeetsFinished, but kept mounted so useOverlayState is stable */}
      <Drawer state={audioState}>
        <Drawer.Backdrop variant="blur" className="bg-black/30">
          <Drawer.Content placement="bottom">
            <Drawer.Dialog>
              <Drawer.Handle />
              <Drawer.Header>
                <Drawer.Heading>Get Audio</Drawer.Heading>
              </Drawer.Header>
              <Drawer.Body>
                <MeetingAudioPlayer meeting_id={record?._id} />
              </Drawer.Body>
              <Drawer.Footer className="flex flex-col gap-3 sm:flex-row">
                <Button
                  className="w-full"
                  variant="secondary"
                  onPress={() => {
                    audioState.close();
                    onAudioDownloadClick();
                  }}
                >
                  <Download className="h-4 w-4" /> Download
                </Button>
                <Button className="w-full" variant="secondary" onPress={audioState.close}>
                  <Mail className="h-4 w-4" /> Email
                </Button>

              </Drawer.Footer>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer>

      {/* Summary drawer — only opened when summaries.length > 0, kept mounted for stable overlay state */}
      <Drawer state={summaryState}>
        <Drawer.Backdrop variant="blur" className="bg-black/30">
          <Drawer.Content placement="bottom">
            <Drawer.Dialog>
              <Drawer.Handle />
              <Drawer.Header>
                <Drawer.Heading>Meeting Summary</Drawer.Heading>
              </Drawer.Header>
              <Drawer.Body>
                <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">
                  {summaries.map((rec) => (
                    <Fragment key={rec["_id"]}>{rec["text"]}</Fragment>
                  ))}
                </pre>
              </Drawer.Body>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer>

      {/* Detailed drawer — only opened when detaileds.length > 0, kept mounted for stable overlay state */}
      <Drawer state={detailedState}>
        <Drawer.Backdrop variant="blur" className="bg-black/30">
          <Drawer.Content placement="bottom"> 
            <Drawer.Dialog className="h-[90vh] flex flex-col">
              <Drawer.Handle />
              <Drawer.Header>
                <Drawer.Heading>Detailed Summary</Drawer.Heading>
              </Drawer.Header>
              <Drawer.Body>
                <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground">
                  {detaileds.map((rec) => (
                    <Fragment key={rec["_id"]}>{rec["text"]}</Fragment>
                  ))}
                </pre>
              </Drawer.Body>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer>

      <OpenInMapsDrawer location={record.location} state={mapsState} />
    </div>
  );
};

export default ViewMeetingDetailsComp;
